<template>
  <div>
    <v-tooltip v-if="areFiltersActiveInStore" top>
      <template #activator="{ on }">
        <v-btn
          class="mr-2"
          icon
          outlined
          small
          depressed
          v-on="on"
          @click="onResetFilters"
        >
          <v-icon x-small>fal fa-times</v-icon>
        </v-btn>
      </template>
      <span>
        Reset all filters
      </span>
    </v-tooltip>
    <v-menu
      v-model="menuVisible"
      :close-on-content-click="false"
      offset-y
      transition="slide-y-transition"
      min-width="100vw"
      nudge-bottom="20"
    >
      <template #activator="{ on }">
        <v-btn
          :outlined="!areFiltersActive && !areFiltersActiveInStore"
          :color="
            !areFiltersActive && !areFiltersActiveInStore
              ? ''
              : $vuetify.theme.dark
              ? 'secondary'
              : 'primary'
          "
          rounded
          small
          depressed
          v-on="on"
        >
          <v-icon class="mr-2" x-small>fal fa-filter</v-icon>
          {{
            areFiltersActive || areFiltersActiveInStore
              ? 'Filters active'
              : 'Show Filters'
          }}
        </v-btn>
      </template>
      <v-card tile>
        <v-card-text class="px-6">
          <div class="py-md-6 px-md-8">
            <!-- Close button -->
            <div class="close-wrapper pa-8">
              <v-btn
                :color="$vuetify.theme.dark ? 'secondary' : 'primary'"
                fab
                x-small
                dark
                depressed
                @click="onClose"
              >
                <v-icon small dark>fal fa-times</v-icon>
              </v-btn>
            </div>
            <!-- End close button -->
            <!-- Full text search -->
            <div class="mb-6">
              <div class="ml-1 mw-320">
                <v-text-field
                  v-model="selectedFilters._searchTerm"
                  prepend-inner-icon="fal fa-search fa-sm"
                  label="Search by keyword"
                />
              </div>
            </div>
            <!-- End Full text search -->
            <!-- Photographer Level -->
            <div class="mb-6">
              <h5 class="ml-1 mb-3 text-subtitle-2 font-weight-bold">
                Photographer level
              </h5>
              <div>
                <v-btn
                  v-for="(s, i) in photographerLevelValues"
                  :key="i"
                  :input-value="s.value === selectedFilters._photographerLevel"
                  :outlined="s.value !== selectedFilters._photographerLevel"
                  :active-class="$vuetify.theme.dark ? 'secondary' : 'primary'"
                  depressed
                  rounded
                  small
                  class="ma-1"
                  @click="onChangePhotographerLevel(s.value)"
                >
                  {{ s.text }}
                </v-btn>
              </div>
            </div>
            <!-- End Photographer Level -->
            <!-- Critique Focus -->
            <div class="mb-6">
              <h5 class="ml-1 mb-3 text-subtitle-2 font-weight-bold">
                Focus
              </h5>
              <div>
                <v-btn
                  v-for="(r, i) in critiqueFocusValues"
                  :key="i"
                  :input-value="r.value === selectedFilters._critiqueFocus"
                  :outlined="r.value !== selectedFilters._critiqueFocus"
                  :active-class="$vuetify.theme.dark ? 'secondary' : 'primary'"
                  depressed
                  rounded
                  small
                  class="ma-1"
                  @click="onChangeCritiqueFocus(r.value)"
                >
                  {{ r.text }}
                </v-btn>
              </div>
            </div>
            <!-- End Critique Focus -->
            <!-- Categories -->
            <div>
              <h5 class="ml-1 mb-3 text-subtitle-2 font-weight-bold">
                Category
              </h5>
              <div>
                <v-btn
                  v-for="(c, i) in categories"
                  :key="i"
                  :input-value="c.value === selectedFilters._categoryId"
                  :outlined="c.value !== selectedFilters._categoryId"
                  :active-class="$vuetify.theme.dark ? 'secondary' : 'primary'"
                  depressed
                  rounded
                  small
                  class="ma-1"
                  @click="onChangeCategoryId(c.value)"
                >
                  {{ c.text }}
                </v-btn>
              </div>
            </div>
            <!-- End categories -->
            <v-divider class="mx-1 my-8" />
            <div class="px-1">
              <v-btn
                v-if="areFiltersActive || areFiltersActiveInStore"
                outlined
                depressed
                class="mr-3"
                @click="onClear"
              >
                Clear
              </v-btn>
              <v-btn
                :class="$vuetify.theme.dark ? 'secondary' : 'primary'"
                depressed
                @click="_onApply"
              >
                Apply
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import types from '@/utils/types'

export default {
  name: 'CritiquesFilters',
  props: {
    categories: {
      type: Array,
      required: true
    },
    onApply: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      types,
      menuVisible: false,
      selectedFilters: {
        _searchTerm: '',
        _photographerLevel: '',
        _critiqueFocus: '0',
        _categoryId: ''
      }
    }
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      searchTerm: state => state.critiques.searchTerm,
      photographerLevel: state => state.critiques.photographerLevel,
      critiqueFocus: state => state.critiques.critiqueFocus,
      categoryId: state => state.critiques.categoryId
    }),
    areFiltersActive() {
      return (
        this.selectedFilters._searchTerm ||
        this.selectedFilters._photographerLevel ||
        this.selectedFilters._critiqueFocus ||
        this.selectedFilters._categoryId !== '0')
    },
    areFiltersActiveInStore() {
      return (
        this.searchTerm ||
        this.photographerLevel ||
        this.critiqueFocus ||
        this.categoryId !== '0'
      )
    },
    photographerLevelValues() {
      const options = [
        { text: 'Pro', value: 'pro' },
        { text: 'Intermediate', value: 'intermediate' },
        { text: 'Beginner', value: 'beginner' }
      ]
      return options
    },
    critiqueFocusValues() {
      const options = [
        { text: 'Composition', value: 'composition' },
        { text: 'Technical', value: 'technical' },
        { text: 'Impact', value: 'impact' },
        { text: 'General', value: 'general' }
      ]
      return options
    }
  },
  mounted() {
    this.selectedFilters._searchTerm = this.searchTerm
    this.selectedFilters._photographerLevel = this.photographerLevel
    this.selectedFilters._critiqueFocus = this.critiqueFocus
    this.selectedFilters._categoryId = this.categoryId
  },
  methods: {
    onChangePhotographerLevel(value) {
      this.selectedFilters._photographerLevel = value
    },
    onChangeCritiqueFocus(value) {
      this.selectedFilters._critiqueFocus = value
    },
    onChangeCategoryId(value) {
      this.selectedFilters._categoryId = value
    },
    onClose() {
      this.menuVisible = false
    },
    onClear() {
      this.selectedFilters._searchTerm = ''
      this.selectedFilters._photographerLevel = ''
      this.selectedFilters._critiqueFocus = ''
      this.selectedFilters._categoryId = '0'
      this.$store.commit('critiques/SET_FILTERS', this.selectedFilters)
    },
    onResetFilters() {
      this.onClear()
      this._onApply()
    },
    _onApply() {
      this.onApply(this.selectedFilters)
      this.menuVisible = false
    }
  }
}
</script>

<style scoped>
.close-wrapper {
  position: absolute;
  top: 0;
  right: 0;
}
.mw-320 {
  max-width: 320px;
}
</style>
